import { Button } from '@/components/ui/button';
import { PageHeader, PageShell } from '@/components/PageShell';
import { Form } from '@/components/ui/form';
import { useBusyAction } from '@/db/useBusyAction';
import { AppInputField } from '@/form/FormUI';
import { extractFieldErrors } from '@/util/extractFieldErrors';
import logger from '@/util/logger';
import { zodResolver } from '@hookform/resolvers/zod';
import { SpaceDao } from '@rock/db-admin';
import { type Space, type SpaceId, SpaceIdSchema } from '@rock/types';
import { type FC, useCallback } from 'react';
import { type FieldErrors, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import * as z from 'zod';

const log = logger('NewSpacePage');
const spaceDao = new SpaceDao();

/**
 * The page behind the Spaces list's "Add Space" button. Only the id is asked for — it is the one
 * field that can't be changed afterwards, so the new space starts as a draft and everything else is
 * filled in on its Details tab.
 */
export const NewSpacePage: FC = () => {
    const navigate = useNavigate();
    const { busy: loading, run } = useBusyAction(log);
    const form = useForm({
        resolver: zodResolver(z.object({ id: SpaceIdSchema.min(3, 'Enter an id of at least 3 characters') })),
        defaultValues: { id: '' as SpaceId },
    });

    const onInvalid = useCallback((errors: FieldErrors) => {
        toast.error('Validation Errors', {
            description: extractFieldErrors(errors).join(', '),
        });
    }, []);

    async function saveNewSpace(spaceId: SpaceId) {
        await run('Create space', async () => {
            // Creating is a whole-document write, so an existing id would lose its content and be unpublished.
            if (await spaceDao.hasSpace(spaceId)) {
                throw new Error(
                    `A space with the id "${spaceId}" already exists. Choose another id, or edit that space.`,
                );
            }
            // Placeholders, not blanks: `SpaceSchema` requires a 3-char name and system title and at
            // least one intro paragraph, and a space that fails to parse is dropped on read — it
            // would vanish from the nav and from its own Details tab, with no way to repair it.
            const space: Space = {
                object: 'space',
                schema: 1,
                id: spaceId,
                published: false,
                groupId: 'whole',
                name: spaceId,
                systemTitle: spaceId,
                icon: '',
                intro: [{ text: 'TODO: write this space’s intro.' }],
            };
            await spaceDao.setSpace(spaceId, space);

            toast.success('Space Created', { description: 'Fill in its name, icon and intro below.' });
            log.info(`Created Space: ${spaceId}`);
            navigate(`/spaces/${spaceId}/details`, { replace: true });
        });
    }

    return (
        <PageShell>
            <PageHeader
                title="Create New Space"
                backLabel="Back to spaces"
                description="Enter the id of the new space — it can't be changed later. The space starts out as a draft; fill in its name, group, icon and intro on the Details tab."
            />

            <Form {...form}>
                <form
                    onSubmit={form.handleSubmit((s) => saveNewSpace(s.id), onInvalid)}
                    className="flex max-w-lg flex-col items-stretch gap-4"
                >
                    <AppInputField
                        label="ID"
                        placeholder="Unique identifier (not editable after create)"
                        control={form.control}
                        name="id"
                        disabled={loading}
                    />
                    <div>
                        <Button type="submit" disabled={loading} className="cursor-pointer">
                            Create Space
                        </Button>
                    </div>
                </form>
            </Form>
        </PageShell>
    );
};
